import React, { useEffect, useState } from 'react';
import Layout from '../../elements/Layout';
import { IoIosSearch } from 'react-icons/io';
import { fetchProperties } from '../../utils/api/api';
import toast from 'react-hot-toast';
import { CSVLink } from 'react-csv';
import PropertyModal from './PropertyModal';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaEdit,
    FaChevronLeft,
    FaChevronRight,
    FaEye,
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaTimesCircle,
    FaInfoCircle,
    FaFilter,
    FaFileDownload,
    FaPlus,
    FaHome,
    FaList,
    FaSortAmountDown,
    FaSortAmountUpAlt,
    FaEllipsisH
} from 'react-icons/fa';

const headers = [
    { label: 'Title', key: 'title' },
    { label: 'Type', key: 'type' },
    { label: 'Description', key: 'description' },
    { label: 'Owner', key: 'owner' },
    { label: 'Assigned To', key: 'assigned_to' },
    { label: 'Location', key: 'location' },
    { label: 'Price', key: 'price' },
    { label: 'Size', key: 'size' },
    { label: 'Status', key: 'status' },
    { label: 'Date Listed', key: 'createdAt' },
];

const Properties = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [stats, setStats] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [propertiesPerPage] = useState(40);
    const [totalPages, setTotalPages] = useState(1);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortField, setSortField] = useState('createdAt');
    const navigate = useNavigate();

    const statusIcons = {
        available: <FaCheckCircle className="text-green-600" size={20} />,
        under_contract: <FaClock className="text-yellow-600" size={20} />,
        warning: <FaExclamationTriangle className="text-orange-600" size={20} />,
        sold: <FaTimesCircle className="text-red-600" size={20} />,
        info: <FaInfoCircle className="text-blue-600" size={20} />,
    };

    const statusLabels = {
        available: "Available",
        under_contract: "Under Contract",
        sold: "Sold",
    };

    const getProperties = async (page, size, search = '') => {
        setLoading(true);
        try {
            const data = await fetchProperties({ page, pageSize: size, search });
            setProperties(data.properties);
            setFilteredProperties(data.properties);
            setTotalPages(data.meta.totalPages);
            setStats(data.stats || []);
        } catch (err) {
            toast.error('Failed to fetch properties.');
        } finally {
            setLoading(false);
        }
    };    

    useEffect(() => {
        getProperties(currentPage, propertiesPerPage, searchTerm);
    }, [currentPage, searchTerm]);    

    const handleSearch = (e) => {
        setSearchTerm(e.target.value.toLowerCase());
    };    

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleRowClick = (property) => {
        setSelectedProperty(property);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProperty(null);
    };

    const handleEditClick = (property) => {
        setSelectedProperty(property);
        setIsModalOpen(true);
    };

    const handleSave = (updatedProperty) => {
        setIsModalOpen(false);
        toast.success('Property updated successfully.');
    };

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const sortedProperties = [...filteredProperties].sort((a, b) => {
        if (sortField === 'createdAt') {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        }
        // Add more sort fields as needed
        return 0;
    });

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 300, damping: 24 }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 24
            }
        },
        hover: {
            y: -5,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            transition: {
                type: "spring",
                stiffness: 500,
                damping: 15
            }
        }
    };

    // For skeleton loading
    const Skeleton = ({ className }) => (
        <motion.div
            initial={{ opacity: 0.5 }}
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className={`bg-gray-200 rounded-lg ${className}`}
        />
    );

    return (
        <Layout text={"Properties"}>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="space-y-6"
            >
                {/* Stats Cards */}
                <motion.div
                    variants={itemVariants}
                    className="grid grid-cols-2 md:grid-cols-4 gap-6"
                >
                    {stats.length > 0 ? (
                        stats.slice(0, 4).map((stat, index) => (
                            <motion.div
                                key={index}
                                whileHover="hover"
                                variants={cardVariants}
                                className="rounded-2xl overflow-hidden shadow-sm"
                            >
                                <div className="relative h-full">
                                    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white z-0"></div>
                                    {index === 0 && <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-green-100/70 z-0"></div>}
                                    {index === 1 && <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-yellow-100/70 z-0"></div>}
                                    {index === 2 && <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-red-100/70 z-0"></div>}
                                    {index === 3 && <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100/70 z-0"></div>}

                                    <div className="relative z-10 p-6">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2 mb-3">
                                                    <div className={`p-2 rounded-xl shadow-sm ${index === 0 ? 'bg-green-500 text-white' :
                                                            index === 1 ? 'bg-yellow-500 text-white' :
                                                                index === 2 ? 'bg-red-500 text-white' :
                                                                    'bg-blue-500 text-white'
                                                        }`}>
                                                        {statusIcons[stat.status?.toLowerCase()] || <FaHome size={20} />}
                                                    </div>
                                                    <h3 className="text-sm font-medium text-gray-800 capitalize">
                                                        {stat.status ? statusLabels[stat.status] || stat.status : "Total Properties"}
                                                    </h3>
                                                </div>
                                                <p className="text-3xl font-bold text-gray-800">{stat.total || stat.total_items}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    ) : (
                        // Skeleton loading for stats
                        Array(4).fill().map((_, i) => (
                            <motion.div
                                key={i}
                                variants={itemVariants}
                                className="rounded-2xl overflow-hidden shadow-sm bg-white/80 backdrop-blur-sm p-6 h-32"
                            >
                                <Skeleton className="h-6 w-32 mb-3" />
                                <Skeleton className="h-10 w-20" />
                            </motion.div>
                        ))
                    )}
                </motion.div>

                {/* Search and Controls */}
                <motion.div
                    variants={itemVariants}
                    className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm"
                >
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative flex-1 sm:w-80"
                        >
                            <input
                                type="text"
                                placeholder="Search properties..."
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                            />
                            <IoIosSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
                        </motion.div>

                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="relative"
                        >
                            <motion.button
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                                <FaFilter size={16} />
                                <span className="hidden sm:inline">Filters</span>
                            </motion.button>

                            <AnimatePresence>
                                {isFilterOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute right-0 z-40 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 p-4"
                                    >
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter Properties</h3>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                                <select
                                                    className="w-full p-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                                                >
                                                    <option value="">All Statuses</option>
                                                    <option value="available">Available</option>
                                                    <option value="under_contract">Under Contract</option>
                                                    <option value="sold">Sold</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Sort By</label>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => toggleSort('createdAt')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${sortField === 'createdAt'
                                                                ? 'bg-secondary text-black'
                                                                : 'bg-gray-100 text-gray-700'
                                                            }`}
                                                    >
                                                        Date
                                                        {sortField === 'createdAt' && (
                                                            sortOrder === 'asc' ? <FaSortAmountUpAlt size={10} /> : <FaSortAmountDown size={10} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="flex justify-end">
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => {
                                                        setSortField('createdAt');
                                                        setSortOrder('desc');
                                                        setIsFilterOpen(false);
                                                    }}
                                                    className="px-3 py-1.5 text-xs text-gray-700 bg-gray-100 rounded-lg mr-2"
                                                >
                                                    Reset
                                                </motion.button>
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => setIsFilterOpen(false)}
                                                    className="px-3 py-1.5 text-xs text-white bg-secondary rounded-lg"
                                                >
                                                    Apply
                                                </motion.button>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <CSVLink
                                data={properties}
                                headers={headers}
                                filename={"properties.csv"}
                                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                            >
                                <FaFileDownload size={16} />
                                <span>Export</span>
                            </CSVLink>
                        </motion.div>

                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/dashboard/properties/new')}
                            className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-white rounded-xl hover:bg-secondary/90 transition-colors"
                        >
                            <FaPlus size={16} />
                            <span>Add Property</span>
                        </motion.button>
                    </div>
                </motion.div>

                {/* Properties Table */}
                <motion.div
                    variants={itemVariants}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
                >
                    {loading ? (
                        <div className="p-8 flex justify-center items-center">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full"
                            />
                        </div>
                    ) : filteredProperties.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16">
                            <FaHome size={64} className="text-gray-300 mb-4" />
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Properties Found</h3>
                            <p className="text-gray-500 mb-6">Try adjusting your search criteria</p>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate('/dashboard/properties/new')}
                                className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-xl"
                            >
                                <FaPlus size={14} />
                                Add New Property
                            </motion.button>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plot No.</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                            <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {sortedProperties.map((property, index) => (
                                            <motion.tr
                                                key={property.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.03 }}
                                                whileHover={{ backgroundColor: "#f9fafb" }}
                                                className="group bg-white cursor-pointer"
                                                onClick={() => handleRowClick(property)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {index + 1 + (currentPage - 1) * propertiesPerPage}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${property.status === 'available'
                                                                ? 'bg-green-100 text-green-800'
                                                                : property.status === 'sold'
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : property.status === 'under_contract'
                                                                        ? 'bg-yellow-100 text-yellow-800'
                                                                        : 'bg-gray-100 text-gray-800'
                                                            }`}
                                                    >
                                                        <span className="mr-1">
                                                            {property.status === 'available' && <FaCheckCircle size={12} />}
                                                            {property.status === 'sold' && <FaTimesCircle size={12} />}
                                                            {property.status === 'under_contract' && <FaClock size={12} />}
                                                        </span>
                                                        {property.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {property.property_number}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {property.size || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {property.sale_info ? (
                                                        <div className="flex items-center">
                                                            <div className="h-8 w-8 flex-shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                                                                {property?.sale_info?.order?.customer?.first_name?.[0]}
                                                            </div>
                                                            <div className="ml-3">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {property?.sale_info?.order?.customer?.first_name?.split(' ')?.slice(0, 2)?.join(' ')}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-gray-500">N/A</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <motion.button
                                                            whileHover={{ scale: 1.1 }}
                                                            whileTap={{ scale: 0.9 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/dashboard/properties/${property.id}`);
                                                            }}
                                                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                                        >
                                                            <FaEye size={16} />
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{ scale: 1.1 }}
                                                            whileTap={{ scale: 0.9 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditClick(property);
                                                            }}
                                                            className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                                                        >
                                                            <FaEdit size={16} />
                                                        </motion.button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 0 && (
                                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => paginate(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                                                    ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                                                    : 'text-gray-700 bg-white hover:bg-gray-50'
                                                }`}
                                        >
                                            Previous
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => paginate(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                                                    ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                                                    : 'text-gray-700 bg-white hover:bg-gray-50'
                                                }`}
                                        >
                                            Next
                                        </motion.button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing <span className="font-medium">{((currentPage - 1) * propertiesPerPage) + 1}</span> to{' '}
                                                <span className="font-medium">
                                                    {Math.min(currentPage * propertiesPerPage, filteredProperties.length)}
                                                </span>{' '}
                                                of <span className="font-medium">{filteredProperties.length}</span> properties
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => paginate(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className={`relative inline-flex items-center px-3 py-2 rounded-l-lg text-sm font-medium ${currentPage === 1
                                                            ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                                                            : 'text-gray-700 bg-white hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <FaChevronLeft className="h-4 w-4" />
                                                </motion.button>

                                                {totalPages > 5 && currentPage > 3 && (
                                                    <>
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={() => paginate(1)}
                                                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                        >
                                                            1
                                                        </motion.button>
                                                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                                                            <FaEllipsisH className="h-3 w-3" />
                                                        </span>
                                                    </>
                                                )}

                                                {[...Array(totalPages)].map((_, i) => {
                                                    const page = i + 1;
                                                    if (
                                                        page === 1 ||
                                                        page === totalPages ||
                                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                                    ) {
                                                        return (
                                                            <motion.button
                                                                key={page}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                onClick={() => paginate(page)}
                                                                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                                                                        ? 'z-10 bg-primary text-white'
                                                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                {page}
                                                            </motion.button>
                                                        );
                                                    }
                                                    return null;
                                                })}

                                                {totalPages > 5 && currentPage < totalPages - 2 && (
                                                    <>
                                                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                                                            <FaEllipsisH className="h-3 w-3" />
                                                        </span>
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={() => paginate(totalPages)}
                                                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                        >
                                                            {totalPages}
                                                        </motion.button>
                                                    </>
                                                )}

                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => paginate(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className={`relative inline-flex items-center px-3 py-2 rounded-r-lg text-sm font-medium ${currentPage === totalPages
                                                            ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                                                            : 'text-gray-700 bg-white hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <FaChevronRight className="h-4 w-4" />
                                                </motion.button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </motion.div>
            </motion.div>

            <AnimatePresence>
                {isModalOpen && selectedProperty && (
                    <PropertyModal
                        isOpen={isModalOpen}
                        property={selectedProperty}
                        onClose={closeModal}
                        onSave={handleSave}
                    />
                )}
            </AnimatePresence>
        </Layout>
    );
};

export default Properties;